import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertActionLink,
  Button,
  Content,
  Form,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  InputGroup,
  InputGroupItem,
  InputGroupText,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  NumberInput,
  Split,
  SplitItem,
  Stack,
  StackItem,
  TextInput,
} from '@patternfly/react-core';
import { MinusCircleIcon, PlusCircleIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../../api';
import type {
  ApiResponseDnsRrsetWrite,
  DnsRrsetWriteResult,
  RecordContentError,
} from '../../../types/api/ApiResponseDomainName';
import type { ZoneRecord } from '../../../types/models/Zone';
import { WRITABLE_RECORD_TYPES, displayRecordName, isApexName, toFqdn } from './zoneRecords';

interface RecordSetFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  /** The RRSet being edited (mode==='edit'); null for create. */
  record: ZoneRecord | null;
  /** Domain id for the path /datacenters/domain-names/{domainId}/dns/rrset. */
  domainId: number;
  /** Zone apex (the domain name) — drives the name suffix, apex detection, and toFqdn. */
  zoneApex: string;
  /** The live zone records, for collision detection (create → offer in-place edit). */
  existingRecords: ZoneRecord[];
  onClose: () => void;
  onSuccess: (result: DnsRrsetWriteResult, op: 'created' | 'updated') => void;
}

/** One editable content row. A stable id keeps inputs from scrambling when a middle row is removed. */
interface ContentLine {
  id: string;
  value: string;
}

interface RrsetFieldErrors {
  name?: string;
  type?: string;
  ttl?: string;
}

const DEFAULT_TYPE = WRITABLE_RECORD_TYPES[0] ?? 'A';
const DEFAULT_TTL = 3600;

// Light client shape only — the backend RecordContentValidator is authoritative and returns the
// precise per-line errors we map back. IPv4: four dot-separated number groups (ranges unchecked).
const IPV4_RE = /^(\d{1,3}\.){3}\d{1,3}$/;
// IPv6: hex groups, colons, and dots (for embedded IPv4 like ::ffff:192.0.2.1); must contain at
// least one colon. Intentionally lenient — the backend RecordContentValidator is authoritative.
const IPV6_RE = /^[0-9a-fA-F:.]+$/;

/** Light per-line client check by type. Non-empty is required; A/AAAA get a loose shape check. */
function isLightLineValid(type: string, value: string): boolean {
  const v = value.trim();
  if (v === '') {
    return false;
  }
  if (type === 'A') {
    return IPV4_RE.test(v);
  }
  if (type === 'AAAA') {
    return IPV6_RE.test(v) && v.includes(':');
  }
  return true;
}

/** Client-side message for a line that fails its light check (server errors override this). */
function lineClientMessage(type: string): string {
  if (type === 'A') {
    return 'Enter a valid IPv4 address.';
  }
  if (type === 'AAAA') {
    return 'Enter a valid IPv6 address.';
  }
  return 'This value is required.';
}

function RecordSetFormModal({
  isOpen,
  mode,
  record,
  domainId,
  zoneApex,
  existingRecords,
  onClose,
  onSuccess,
}: RecordSetFormModalProps) {
  const apiFetch = useApiFetch();

  // Monotonic counter for stable content-row ids (so removing a middle row keeps inputs aligned).
  const idCounter = useRef<number>(0);
  const nextId = (): string => {
    idCounter.current += 1;
    return `content-line-${idCounter.current}`;
  };

  const [name, setName] = useState<string>('');
  const [type, setType] = useState<string>(DEFAULT_TYPE);
  const [ttl, setTtl] = useState<number>(DEFAULT_TTL);
  const [contentLines, setContentLines] = useState<ContentLine[]>([]);

  // Edit-mode baselines for the hasChanges gate (type is immutable, so it is not tracked).
  const [originalName, setOriginalName] = useState<string>('');
  const [originalTtl, setOriginalTtl] = useState<number>(DEFAULT_TTL);
  const [originalContent, setOriginalContent] = useState<string[]>([]);

  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<RrsetFieldErrors>({});
  const [lineErrors, setLineErrors] = useState<Record<string, string>>({});
  const [contentSetError, setContentSetError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // The existing RRSet the user accepted converting into editing (collision → in-place edit); null normally.
  const [convertedTarget, setConvertedTarget] = useState<ZoneRecord | null>(null);

  const apexNoDot = zoneApex.replace(/\.$/, '');
  const isEdit = mode === 'edit';
  const isCname = type === 'CNAME';

  // "Effective edit": opened in edit mode OR converted from a create collision. editTarget is the RRSet
  // being edited in either case (null while genuinely creating).
  const editTarget = convertedTarget ?? (isEdit ? record : null);
  const isEditing = editTarget !== null;

  // Seed form state from the entity (edit) or to fresh defaults (create) whenever the modal opens
  // or the target record changes. Errors are cleared on every (re)seed.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setFormError(null);
    setFieldErrors({});
    setLineErrors({});
    setContentSetError(null);
    setIsSubmitting(false);
    setConvertedTarget(null);

    if (isEdit && record) {
      const relativeName = displayRecordName(record.name, zoneApex, 'relative');
      const lines: ContentLine[] =
        record.content.length > 0
          ? record.content.map((value) => ({ id: nextId(), value }))
          : [{ id: nextId(), value: '' }];
      setName(relativeName);
      setType(record.type.toUpperCase());
      setTtl(record.ttl);
      setContentLines(lines);
      setOriginalName(relativeName);
      setOriginalTtl(record.ttl);
      setOriginalContent(record.content.map((v) => v.trim()));
    } else {
      setName('');
      setType(DEFAULT_TYPE);
      setTtl(DEFAULT_TTL);
      setContentLines([{ id: nextId(), value: '' }]);
      setOriginalName('');
      setOriginalTtl(DEFAULT_TTL);
      setOriginalContent([]);
    }
    // nextId/zoneApex/isEdit are stable for a given open; re-seeding is keyed on the inputs below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, record]);

  const updateLine = (id: string, value: string) => {
    setContentLines((prev) => prev.map((l) => (l.id === id ? { ...l, value } : l)));
    setLineErrors((prev) => {
      if (!prev[id]) {
        return prev;
      }
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setContentSetError(null);
  };

  const addLine = () => {
    setContentLines((prev) => [...prev, { id: nextId(), value: '' }]);
    setContentSetError(null);
  };

  const removeLine = (id: string) => {
    setContentLines((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.id !== id)));
    setLineErrors((prev) => {
      if (!prev[id]) {
        return prev;
      }
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setContentSetError(null);
  };

  const handleTypeChange = (value: string) => {
    setType(value);
    setFieldErrors((prev) => {
      if (!prev.type) {
        return prev;
      }
      const next = { ...prev };
      delete next.type;
      return next;
    });
    setLineErrors({});
    setContentSetError(null);
    // A CNAME RRSet holds exactly one value — collapse to the first row when switching to CNAME.
    if (value === 'CNAME') {
      setContentLines((prev) => [prev[0] ?? { id: nextId(), value: '' }]);
    }
  };

  const clearFieldError = (field: keyof RrsetFieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Live collision detection (create only): does the typed (name, type) already exist as an RRSet?
  const matchedExisting = useMemo<ZoneRecord | null>(() => {
    if (isEdit || convertedTarget !== null) {
      return null; // only while genuinely creating
    }
    if (name.trim() === '') {
      return null; // don't suggest on the pristine/blank-apex state
    }
    const fqdn = toFqdn(name, zoneApex).replace(/\.$/, '').toLowerCase();
    const upper = type.toUpperCase();
    return (
      existingRecords.find(
        (r) => r.type.toUpperCase() === upper && r.name.replace(/\.$/, '').toLowerCase() === fqdn,
      ) ?? null
    );
  }, [isEdit, convertedTarget, name, type, zoneApex, existingRecords]);

  // The apex NS set can't be edited here (backend blocks it), so never offer to convert into editing it.
  const matchedIsApexNs =
    matchedExisting !== null &&
    matchedExisting.type.toUpperCase() === 'NS' &&
    isApexName(matchedExisting.name, zoneApex);
  const canConvert = matchedExisting !== null && !matchedIsApexNs;

  // Convert the create dialog in place into an edit of the matched set, merging the user's draft values
  // with the existing ones (deduped, existing first) so the value they were adding isn't lost.
  const convertToEdit = () => {
    if (matchedExisting === null || matchedIsApexNs) {
      return;
    }
    const target = matchedExisting;
    const relativeName = displayRecordName(target.name, zoneApex, 'relative');

    const draft = contentLines.map((l) => l.value.trim()).filter((v) => v.length > 0);
    const isCnameTarget = target.type.toUpperCase() === 'CNAME';
    // Trim existing values so the dedup below compares like-for-like with the trimmed draft, and so the
    // loaded lines match the (also-trimmed) hasChanges baseline (originalContent) set below.
    const mergedValues = target.content.map((v) => v.trim());
    if (!isCnameTarget) {
      for (const v of draft) {
        if (!mergedValues.includes(v)) {
          mergedValues.push(v);
        }
      }
    }
    const lines: ContentLine[] = (mergedValues.length > 0 ? mergedValues : ['']).map((value) => ({
      id: nextId(),
      value,
    }));

    setConvertedTarget(target);
    setName(relativeName);
    setType(target.type.toUpperCase());
    setTtl(target.ttl);
    setContentLines(lines);
    // Baselines = the REAL existing set, so hasChanges correctly detects the added value(s).
    setOriginalName(relativeName);
    setOriginalTtl(target.ttl);
    setOriginalContent(target.content.map((v) => v.trim()));
    setFormError(null);
    setFieldErrors({});
    setLineErrors({});
    setContentSetError(null);
  };

  // Validity (light — the server is authoritative). Name is never invalid client-side: empty/@ = apex
  // and any other value is a subdomain; toFqdn always yields a valid FQDN. We only reject INTERNAL
  // whitespace (leading/trailing is trimmed by toFqdn), so the error state agrees with the preview.
  const isNameValid = !/\s/.test(name.trim());
  const isTtlValid = Number.isInteger(ttl) && ttl >= 0;
  const nonEmptyLines = contentLines.map((l) => l.value.trim()).filter((v) => v.length > 0);
  const allNonEmptyLinesValid = contentLines.every(
    (l) => l.value.trim() === '' || isLightLineValid(type, l.value),
  );
  const cnameCardinalityOk = !isCname || nonEmptyLines.length === 1;
  const isContentValid = nonEmptyLines.length >= 1 && allNonEmptyLinesValid && cnameCardinalityOk;

  // hasChanges (edit only). Compare the FQDN (normalized), TTL, and the non-empty content lines.
  const currentFqdnNorm = toFqdn(name, zoneApex).replace(/\.$/, '').toLowerCase();
  const originalFqdnNorm = (editTarget !== null ? editTarget.name : toFqdn(originalName, zoneApex))
    .replace(/\.$/, '')
    .toLowerCase();
  const nameChanged = currentFqdnNorm !== originalFqdnNorm;
  const ttlChanged = ttl !== originalTtl;
  const contentChanged = JSON.stringify(nonEmptyLines) !== JSON.stringify(originalContent);
  const hasChanges = nameChanged || ttlChanged || contentChanged;

  const isFormValid = isNameValid && isTtlValid && isContentValid && (!isEditing || hasChanges);

  const resetAndClose = () => {
    setName('');
    setType(DEFAULT_TYPE);
    setTtl(DEFAULT_TTL);
    setContentLines([]);
    setOriginalName('');
    setOriginalTtl(DEFAULT_TTL);
    setOriginalContent([]);
    setFormError(null);
    setFieldErrors({});
    setLineErrors({});
    setContentSetError(null);
    setIsSubmitting(false);
    setConvertedTarget(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (isEdit && !record) {
      return;
    }
    setIsSubmitting(true);
    setFormError(null);
    setFieldErrors({});
    setLineErrors({});
    setContentSetError(null);

    const fqdn = toFqdn(name, zoneApex);
    // Keep the id of each surviving line so a server RecordContentError.index (a position in the
    // SENT array, which is the empty-filtered list) maps back to the exact row — blank rows included.
    const submitted = contentLines
      .map((l) => ({ id: l.id, value: l.value.trim() }))
      .filter((l) => l.value.length > 0);
    const content = submitted.map((l) => l.value);

    const path = `/datacenters/domain-names/${domainId}/dns/rrset`;
    let method: 'POST' | 'PATCH';
    const payload: Record<string, unknown> = { name: fqdn, type, ttl, content };

    if (editTarget !== null) {
      method = 'PATCH';
      const originalFqdn = editTarget.name; // already the canonical FQDN
      payload.name = originalFqdn;
      // Rename only when the name actually changed (compare normalized FQDNs).
      if (fqdn.replace(/\.$/, '').toLowerCase() !== originalFqdn.replace(/\.$/, '').toLowerCase()) {
        payload.new_name = fqdn;
      }
    } else {
      method = 'POST';
    }

    try {
      const response = await apiFetch(path, {
        method,
        headers: { Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      let body: ApiResponseDnsRrsetWrite | null;
      try {
        body = (await response.json()) as ApiResponseDnsRrsetWrite;
      } catch {
        body = null;
      }

      if (response.ok && body?.status === 'success' && body.data?.record_set) {
        onSuccess(body.data.record_set, editTarget !== null ? 'updated' : 'created');
        resetAndClose();
        return;
      }

      // Failure: HTTP 200 + status:'error' with a field→error map under data (no 422/fail flow).
      const data = body?.data;
      const nextFieldErrors: RrsetFieldErrors = {};
      const nextLineErrors: Record<string, string> = {};
      let nextContentSetError: string | null = null;

      if (data) {
        if (typeof data.name === 'string') {
          nextFieldErrors.name = data.name;
        }
        if (typeof data.type === 'string') {
          nextFieldErrors.type = data.type;
        }
        if (typeof data.ttl === 'string') {
          nextFieldErrors.ttl = data.ttl;
        }
        if (typeof data.content === 'string') {
          // Body-shape error ("content must be a non-empty array").
          nextContentSetError = data.content;
        } else if (Array.isArray(data.content)) {
          const contentErrors = data.content as RecordContentError[];
          for (const e of contentErrors) {
            if (e.index === -1) {
              nextContentSetError = e.message; // set-level (empty set, CNAME cardinality)
            } else if (submitted[e.index]) {
              nextLineErrors[submitted[e.index].id] = e.message; // map by sent-array position
            }
          }
          // Defensive: if no entry mapped to a line or the set (unexpected index semantics), still
          // surface the validator text rather than letting it fall back to the generic message.
          if (nextContentSetError === null && Object.keys(nextLineErrors).length === 0 && contentErrors.length > 0) {
            nextContentSetError = contentErrors.map((e) => e.message).join('; ');
          }
        }
      }

      setFieldErrors(nextFieldErrors);
      setLineErrors(nextLineErrors);
      setContentSetError(nextContentSetError);

      const hasFieldLevel =
        Object.keys(nextFieldErrors).length > 0 ||
        Object.keys(nextLineErrors).length > 0 ||
        nextContentSetError !== null;
      // The top alert is only for messages with no field to point at (policy / SOA / DNS-fault).
      setFormError(
        hasFieldLevel ? null : (body?.message ?? `Request failed with status ${response.status}`.trim()),
      );
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'An unexpected network error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const title =
    editTarget !== null
      ? `Edit record set — ${displayRecordName(editTarget.name, zoneApex, 'relative')}`
      : 'Create record set';
  const ariaLabel = editTarget !== null ? 'Edit record set' : 'Create record set';

  return (
    <Modal variant="medium" isOpen={isOpen} onClose={resetAndClose} aria-label={ariaLabel}>
      <ModalHeader title={title} />
      <ModalBody>
        {formError && (
          <Alert variant="danger" isInline title="Could not save record set" className="pf-v6-u-mb-md">
            {formError}
          </Alert>
        )}
        {canConvert && (
          <Alert
            variant="info"
            isInline
            title={`A ${type} record set for ${toFqdn(name, zoneApex)} already exists`}
            className="pf-v6-u-mb-md"
            actionLinks={<AlertActionLink onClick={convertToEdit}>Load the existing set to edit</AlertActionLink>}
          >
            DNS allows only one {type} record set per name (it currently has {matchedExisting?.content.length ?? 0}{' '}
            value{(matchedExisting?.content.length ?? 0) === 1 ? '' : 's'}). To add another value, edit the existing
            set instead of creating a new one.
          </Alert>
        )}
        {matchedIsApexNs && (
          <Alert
            variant="info"
            isInline
            title={`The apex ${type} record set already exists`}
            className="pf-v6-u-mb-md"
          >
            The zone&apos;s apex {type} record set is managed here automatically and can&apos;t be edited from this
            dialog.
          </Alert>
        )}
        {convertedTarget !== null && (
          <Alert variant="info" isInline title="Editing the existing record set" className="pf-v6-u-mb-md">
            Loaded the existing {convertedTarget.type} record set for <strong>{toFqdn(name, zoneApex)}</strong> — its
            current values are shown below. Add or change values, then Save.
          </Alert>
        )}
        <Form>
          <FormGroup label="Name" isRequired fieldId="rrset-name">
            <InputGroup>
              <InputGroupItem isFill>
                <TextInput
                  id="rrset-name"
                  className="pf-v6-u-text-align-end"
                  value={name}
                  onChange={(_event, value) => {
                    setName(value);
                    clearFieldError('name');
                  }}
                  validated={fieldErrors.name || (name !== '' && !isNameValid) ? 'error' : 'default'}
                />
              </InputGroupItem>
              <InputGroupText>.{apexNoDot}.</InputGroupText>
            </InputGroup>
            {fieldErrors.name ? (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">{fieldErrors.name}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            ) : name !== '' && !isNameValid ? (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">A record name cannot contain spaces.</HelperTextItem>
                </HelperText>
              </FormHelperText>
            ) : (
              <>
                <FormHelperText>
                  <HelperText>
                    <HelperTextItem>
                      Leave blank or enter @ for the zone apex; otherwise enter the subdomain (e.g. www).
                    </HelperTextItem>
                  </HelperText>
                </FormHelperText>
                <Content component="small" className="pf-v6-u-mt-xs">
                  Record name: <strong>{toFqdn(name, zoneApex)}</strong>
                </Content>
              </>
            )}
          </FormGroup>

          <FormGroup label="Type" isRequired fieldId="rrset-type">
            <FormSelect
              id="rrset-type"
              value={type}
              onChange={(_event, value) => handleTypeChange(value)}
              isDisabled={isEditing}
              isRequired
              validated={fieldErrors.type ? 'error' : 'default'}
            >
              {WRITABLE_RECORD_TYPES.map((t) => (
                <FormSelectOption key={t} value={t} label={t} />
              ))}
            </FormSelect>
            {fieldErrors.type ? (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">{fieldErrors.type}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            ) : isEditing ? (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>Type can&apos;t be changed; delete and recreate to change type.</HelperTextItem>
                </HelperText>
              </FormHelperText>
            ) : null}
          </FormGroup>

          <FormGroup label="TTL (seconds)" isRequired fieldId="rrset-ttl">
            <NumberInput
              value={ttl}
              min={0}
              onMinus={() => {
                setTtl((t) => Math.max(0, t - 1));
                clearFieldError('ttl');
              }}
              onPlus={() => {
                setTtl((t) => t + 1);
                clearFieldError('ttl');
              }}
              onChange={(event) => {
                const parsed = parseInt(event.currentTarget.value, 10);
                setTtl(Number.isNaN(parsed) ? 0 : Math.max(0, parsed));
                clearFieldError('ttl');
              }}
              inputProps={{ id: 'rrset-ttl' }}
              inputAriaLabel="TTL in seconds"
              minusBtnAriaLabel="Decrease TTL"
              plusBtnAriaLabel="Increase TTL"
              validated={fieldErrors.ttl ? 'error' : 'default'}
            />
            {fieldErrors.ttl ? (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">{fieldErrors.ttl}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            ) : (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>Time to live, in seconds.</HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>

          <FormGroup label="Values" isRequired fieldId="rrset-content">
            <Stack hasGutter>
              {contentLines.map((line, i) => {
                const showClientError = line.value.trim() !== '' && !isLightLineValid(type, line.value);
                const lineError = lineErrors[line.id] ?? (showClientError ? lineClientMessage(type) : null);
                return (
                  <StackItem key={line.id}>
                    <Split hasGutter>
                      <SplitItem isFilled>
                        <TextInput
                          id={i === 0 ? 'rrset-content' : undefined}
                          type="text"
                          value={line.value}
                          onChange={(_event, value) => updateLine(line.id, value)}
                          aria-label={`Value ${i + 1}`}
                          validated={lineError ? 'error' : 'default'}
                        />
                      </SplitItem>
                      <SplitItem>
                        <Button
                          variant="plain"
                          aria-label={`Remove value ${i + 1}`}
                          icon={<MinusCircleIcon />}
                          onClick={() => removeLine(line.id)}
                          isDisabled={contentLines.length <= 1}
                        />
                      </SplitItem>
                    </Split>
                    {lineError && (
                      <FormHelperText>
                        <HelperText>
                          <HelperTextItem variant="error">{lineError}</HelperTextItem>
                        </HelperText>
                      </FormHelperText>
                    )}
                  </StackItem>
                );
              })}
              {!isCname && (
                <StackItem>
                  <Button variant="link" icon={<PlusCircleIcon />} onClick={addLine}>
                    Add value
                  </Button>
                </StackItem>
              )}
              {contentSetError && (
                <StackItem>
                  <FormHelperText>
                    <HelperText>
                      <HelperTextItem variant="error">{contentSetError}</HelperTextItem>
                    </HelperText>
                  </FormHelperText>
                </StackItem>
              )}
            </Stack>
            <FormHelperText>
              <HelperText>
                <HelperTextItem>
                  {isCname
                    ? 'A CNAME set holds exactly one value.'
                    : 'One value per line. Add several for a round-robin set.'}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={() => void handleSubmit()}
          isLoading={isSubmitting}
          isDisabled={!isFormValid || isSubmitting}
        >
          {isEditing ? 'Save changes' : 'Create'}
        </Button>
        <Button variant="link" onClick={resetAndClose} isDisabled={isSubmitting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default RecordSetFormModal;
